import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Logo from "../../assets/logo.png";
import api from "../../api/axios";

export const Sidebar = () => {
  const location = useLocation();

  const role   = Number(localStorage.getItem("role"));
  const userId = localStorage.getItem("adminId");
  const token  = localStorage.getItem("token");

  const [perms, setPerms]             = useState(null);
  const [orderCounts, setOrderCounts] = useState({
    placed: 0, processing: 0, printing: 0,
    shipped: 0, delivered: 0, hold: 0,
  });

  /* ── Fetch permissions ── */
  useEffect(() => {
    if (role === 2 && userId) {
      api
        .get(`/api/permission/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setPerms(res.data.data?.permissions || {}))
        .catch(() => setPerms({}));
    }
  }, [userId, role, token]);

  /* ── Fetch order counts (extracted into a reusable function) ── */
  const fetchOrderCounts = useCallback(() => {
    const statuses = ["placed", "processing", "printing", "shipped", "delivered", "hold"];
    Promise.all(
      statuses.map((s) =>
        api
          .get(`/api/orders?status=${s}&page=1&limit=1`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => ({
            status: s,
            count: res.data.pagination?.total ?? res.data.total ?? 0,
          }))
          .catch(() => ({ status: s, count: 0 }))
      )
    ).then((results) => {
      const counts = {};
      results.forEach(({ status, count }) => (counts[status] = count));
      setOrderCounts(counts);
    });
  }, [token]);

  // Initial load
  useEffect(() => {
    fetchOrderCounts();
  }, [fetchOrderCounts]);

  // ✅ Re-fetch counts whenever a status is changed elsewhere (e.g. OrderList.jsx)
  useEffect(() => {
    const handler = () => fetchOrderCounts();
    window.addEventListener("order-status-changed", handler);
    return () => window.removeEventListener("order-status-changed", handler);
  }, [fetchOrderCounts]);

  const can = (key) => {
    if (role === 1) return true;
    if (!perms) return false;
    return !!perms[key];
  };

  /* ── Active checks ── */
  const isCategoryActive      = location.pathname.startsWith("/admin/category");
  const isSubCategoryActive   = location.pathname.startsWith("/admin/subcategory");
  const isProductActive       = location.pathname.startsWith("/admin/product");
  const isColorActive         = location.pathname.startsWith("/admin/color");
  const isSizeActive          = location.pathname.startsWith("/admin/size");
  const isOrderActive         = location.pathname.startsWith("/admin/order");
  const isManualOrderActive   = location.pathname.startsWith("/admin/manual-order");
  const isLabelActive         = location.pathname.startsWith("/admin/label");
  const isBannerActive        = location.pathname.startsWith("/admin/banner");
  const isCouponActive        = location.pathname.startsWith("/admin/coupon");
  const isSettingActive       =
    location.pathname.startsWith("/admin/setting") ||
    location.pathname.startsWith("/admin/user");

  /* ── Badge helper ── */
  const CountBadge = ({ count }) =>
    count > 0 ? (
      <span
        style={{
          marginLeft: "auto",
          background: "#f97316",
          color: "#fff",
          borderRadius: "10px",
          fontSize: "11px",
          fontWeight: 600,
          padding: "1px 7px",
          lineHeight: "18px",
          minWidth: "20px",
          textAlign: "center",
          display: "inline-block",
        }}
      >
        {count}
      </span>
    ) : null;

  return (
    <>
      <div className="logo-box">
        <img src={Logo} className="logo-lg" alt="logo" />
      </div>

      <div className="scrollbar">
        <ul className="navbar-nav">

          {/* DASHBOARD */}
          {can("dashboard") && (
            <li className="nav-item">
              <NavLink to="/admin/dashboard" className="nav-link" end>
                <span className="nav-icon">
                  <iconify-icon icon="solar:widget-5-bold-duotone" />
                </span>
                <span className="nav-text">Dashboard</span>
              </NavLink>
            </li>
          )}

          {/* CATEGORY */}
          {can("category") && (
            <li className={`nav-item ${isCategoryActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isCategoryActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarCategory"
              >
                <span className="nav-icon">
                  <iconify-icon icon="solar:shop-bold-duotone" />
                </span>
                <span className="nav-text">Category</span>
              </a>
              <div id="sidebarCategory" className={`collapse ${isCategoryActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">
                  <li className="sub-nav-item">
                    <NavLink to="category/list" className="sub-nav-link">List</NavLink>
                  </li>
                </ul>
              </div>
            </li>
          )}

          {/* SUB CATEGORY */}
          {can("subCategory") && (
            <li className={`nav-item ${isSubCategoryActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isSubCategoryActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarSubCategory"
              >
                <span className="nav-icon">
                  <iconify-icon icon="solar:box-bold-duotone" />
                </span>
                <span className="nav-text">Sub Category</span>
              </a>
              <div id="sidebarSubCategory" className={`collapse ${isSubCategoryActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">
                  <li className="sub-nav-item">
                    <NavLink to="subcategory/list" className="sub-nav-link">List</NavLink>
                  </li>
                </ul>
              </div>
            </li>
          )}

          {/* PRODUCTS */}
          {can("products") && (
            <li className={`nav-item ${isProductActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isProductActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarProducts"
              >
                <span className="nav-icon">
                  <iconify-icon icon="solar:t-shirt-bold-duotone" />
                </span>
                <span className="nav-text">Products</span>
              </a>
              <div id="sidebarProducts" className={`collapse ${isProductActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">
                  <li className="sub-nav-item">
                    <NavLink to="product/list" className="sub-nav-link">List</NavLink>
                  </li>
                  <li className="sub-nav-item">
                    <NavLink to="product/add" className="sub-nav-link">Add</NavLink>
                  </li>
                </ul>
              </div>
            </li>
          )}

          {/* ORDERS */}
          {can("orders") && (
            <li className={`nav-item ${isOrderActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isOrderActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarOrders"
              >
                <span className="nav-icon">
                  <iconify-icon icon="solar:bag-check-bold-duotone" />
                </span>
                <span className="nav-text">Orders</span>
              </a>
              <div id="sidebarOrders" className={`collapse ${isOrderActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">

                  <li className="sub-nav-item">
                    <NavLink to="order/Received" className="sub-nav-link"
                      style={{ display: "flex", alignItems: "center" }}>
                      Order Received
                      <CountBadge count={orderCounts.placed} />
                    </NavLink>
                  </li>

                  <li className="sub-nav-item">
                    <NavLink to="order/Printing" className="sub-nav-link"
                      style={{ display: "flex", alignItems: "center" }}>
                      Printing
                      <CountBadge count={orderCounts.printing} />
                    </NavLink>
                  </li>

                  <li className="sub-nav-item">
                    <NavLink to="order/Processing" className="sub-nav-link"
                      style={{ display: "flex", alignItems: "center" }}>
                      Processing
                      <CountBadge count={orderCounts.processing} />
                    </NavLink>
                  </li>

                  <li className="sub-nav-item">
                    <NavLink to="order/Shipped" className="sub-nav-link"
                      style={{ display: "flex", alignItems: "center" }}>
                      Shipped
                      <CountBadge count={orderCounts.shipped} />
                    </NavLink>
                  </li>

                  <li className="sub-nav-item">
                    <NavLink to="order/hold" className="sub-nav-link"
                      style={{ display: "flex", alignItems: "center" }}>
                      Hold
                      <CountBadge count={orderCounts.hold} />
                    </NavLink>
                  </li>

                  <li className="sub-nav-item">
                    <NavLink to="order/Delivered" className="sub-nav-link"
                      style={{ display: "flex", alignItems: "center" }}>
                      Delivered
                      <CountBadge count={orderCounts.delivered} />
                    </NavLink>
                  </li>

                </ul>
              </div>
            </li>
          )}

          {/* MANUAL ORDERS */}
          {can("orders") && (
            <li className={`nav-item ${isManualOrderActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isManualOrderActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarManualOrders"
              >
                <span className="nav-icon">
                  {/* pencil-and-ruler icon — distinct from the bag icon used for Orders */}
                  <iconify-icon icon="solar:pen-new-square-bold-duotone" />
                </span>
                <span className="nav-text">Manual Orders</span>
              </a>
              <div id="sidebarManualOrders" className={`collapse ${isManualOrderActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">
                  <li className="sub-nav-item">
                    <NavLink to="manual-order/create" className="sub-nav-link">Create Order</NavLink>
                  </li>
                  <li className="sub-nav-item">
                    <NavLink to="manual-order/list" className="sub-nav-link">List</NavLink>
                  </li>
                </ul>
              </div>
            </li>
          )}

          {/* COLOR */}
          {can("color") && (
            <li className={`nav-item ${isColorActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isColorActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarColor"
              >
                <span className="nav-icon">
                  <iconify-icon icon="solar:volleyball-bold-duotone" />
                </span>
                <span className="nav-text">Color</span>
              </a>
              <div id="sidebarColor" className={`collapse ${isColorActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">
                  <li className="sub-nav-item">
                    <NavLink to="color/list" className="sub-nav-link">List</NavLink>
                  </li>
                </ul>
              </div>
            </li>
          )}

          {/* SIZE */}
          {can("size") && (
            <li className={`nav-item ${isSizeActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isSizeActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarSize"
              >
                <span className="nav-icon">
                  <iconify-icon icon="solar:clipboard-list-bold-duotone" />
                </span>
                <span className="nav-text">Size</span>
              </a>
              <div id="sidebarSize" className={`collapse ${isSizeActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">
                  <li className="sub-nav-item">
                    <NavLink to="size/list" className="sub-nav-link">List</NavLink>
                  </li>
                </ul>
              </div>
            </li>
          )}

          {/* LABEL */}
          {can("label") && (
            <li className={`nav-item ${isLabelActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isLabelActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarLabel"
              >
                <span className="nav-icon">
                  <iconify-icon icon="solar:tuning-2-bold-duotone" />
                </span>
                <span className="nav-text">Label</span>
              </a>
              <div id="sidebarLabel" className={`collapse ${isLabelActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">
                  <li className="sub-nav-item">
                    <NavLink to="label/list" className="sub-nav-link">Label</NavLink>
                  </li>
                </ul>
              </div>
            </li>
          )}

          {/* BANNER */}
          {can("banner") && (
            <li className={`nav-item ${isBannerActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isBannerActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarBanner"
              >
                <span className="nav-icon">
                  <iconify-icon icon="solar:gallery-wide-bold-duotone" />
                </span>
                <span className="nav-text">Banner</span>
              </a>
              <div id="sidebarBanner" className={`collapse ${isBannerActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">
                  <li className="sub-nav-item">
                    <NavLink to="banner/list" className="sub-nav-link">List</NavLink>
                  </li>
                </ul>
              </div>
            </li>
          )}

          {/* COUPON */}
          {can("coupon") && (
            <li className={`nav-item ${isCouponActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isCouponActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarCoupon"
              >
                <span className="nav-icon">
                  <iconify-icon icon="solar:ticket-sale-bold-duotone" />
                </span>
                <span className="nav-text">Coupon</span>
              </a>
              <div id="sidebarCoupon" className={`collapse ${isCouponActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">
                  <li className="sub-nav-item">
                    <NavLink to="coupon/list" className="sub-nav-link">List</NavLink>
                  </li>
                </ul>
              </div>
            </li>
          )}

          {/* SETTINGS */}
          {can("settings") && (
            <li className={`nav-item ${isSettingActive ? "active" : ""}`}>
              <a
                className={`nav-link menu-arrow ${isSettingActive ? "active" : ""}`}
                data-bs-toggle="collapse"
                href="#sidebarSetting"
              >
                <span className="nav-icon">
                  <iconify-icon icon="solar:card-send-bold-duotone" />
                </span>
                <span className="nav-text">Settings</span>
              </a>
              <div id="sidebarSetting" className={`collapse ${isSettingActive ? "show" : ""}`}>
                <ul className="nav sub-navbar-nav">
                  {role === 1 && (
                    <>
                      <li className="sub-nav-item">
                        <NavLink to="user/adduser" className="sub-nav-link">Add User</NavLink>
                      </li>
                      <li className="sub-nav-item">
                        <NavLink to="user/permissions" className="sub-nav-link">Permissions</NavLink>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </li>
          )}

        </ul>
      </div>
    </>
  );
};

export default Sidebar;